import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IoChevronDownOutline, IoDocumentTextOutline, IoBookOutline, IoSearchOutline, IoCloseCircleOutline, IoNewspaperOutline } from 'react-icons/io5';
import { Switch } from '@headlessui/react';
import { useDarkMode } from '../../context/DarkModeContext';
import { Tag } from './types';

interface TypeVisibility {
  notes: boolean;
  careerCards: boolean;
  reviews: boolean;
}

interface TagsDropdownProps {
  tags: Tag[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  typeVisibility: TypeVisibility;
  onTypeVisibilityChange: (type: keyof TypeVisibility, value: boolean) => void;
  showTypeSliders?: boolean;
}

const TagsDropdown: React.FC<TagsDropdownProps> = ({
  tags,
  selectedTags,
  onTagsChange,
  typeVisibility,
  onTypeVisibilityChange,
  showTypeSliders = true
}) => {
  const { darkMode } = useDarkMode();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const getDropdownPosition = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      width: rect.width,
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
      zIndex: 9999
    } as const;
  };

  const toggleTag = (tagName: string) => {
    const newSelectedTags = selectedTags.includes(tagName)
      ? selectedTags.filter(tag => tag !== tagName)
      : [...selectedTags, tagName];
    onTagsChange(newSelectedTags);
  };

  const getTagIcon = (types: ('note' | 'card' | 'review')[]) => {
    if (types.length > 1) {
      return (
        <div className="flex -space-x-1">
          {types.includes('note') && <IoBookOutline className="text-green-500" />}
          {types.includes('card') && <IoDocumentTextOutline className="text-blue-500" />}
          {types.includes('review') && <IoNewspaperOutline className="text-purple-500" />}
        </div>
      );
    }
    if (types.includes('note')) {
      return <IoBookOutline className="text-green-500" />;
    }
    if (types.includes('review')) {
      return <IoNewspaperOutline className="text-purple-500" />;
    }
    return <IoDocumentTextOutline className="text-blue-500" />;
  };

  const filteredTags = tags.filter(tag => 
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-2 flex items-center justify-between rounded-lg border ${
          darkMode
            ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        } transition-colors`}
        {...(isOpen ? { 'aria-expanded': 'true' } : { 'aria-expanded': 'false' })}
        aria-haspopup="true"
      >
        <span className="flex items-center gap-2">
          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>Filter by Type and Tags</span>
          {selectedTags.length > 0 && (
            <>
              <span className={`px-2 py-0.5 text-sm rounded-full ${
                darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
              }`}>
                {selectedTags.length}
              </span>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onTagsChange([]);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onTagsChange([]);
                  }
                }}
                className={`p-1 rounded-full cursor-pointer ${
                  darkMode ? 'hover:bg-white/10 text-gray-300' : 'hover:bg-black/10 text-gray-600'
                }`}
                title="Clear all selected tags"
              >
                <IoCloseCircleOutline className="w-4 h-4" />
              </div>
            </>
          )}
        </span>
        <IoChevronDownOutline
          className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={getDropdownPosition()}
          className={`rounded-lg shadow-lg ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
        >
          <div className="p-2">
            {/* Search Input */}
            <div className="relative mb-2">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tags..."
                className={`w-full pl-8 pr-3 py-2 rounded-md border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
              <IoSearchOutline className={`absolute left-2 top-2.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            </div>

            {/* Type Filters */}
            {showTypeSliders && (
              <div className="mb-3 space-y-2 border-b border-gray-200 dark:border-gray-700 pb-3">
                {typeVisibility.notes !== undefined && (
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <IoBookOutline className="text-green-500" />
                      <span className={darkMode ? 'text-white' : 'text-gray-900'}>Notes</span>
                    </div>
                    <Switch
                      checked={typeVisibility.notes}
                      onChange={(checked) => onTypeVisibilityChange('notes', checked)}
                      className={`${
                        typeVisibility.notes ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                      } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          typeVisibility.notes ? 'translate-x-5' : 'translate-x-1'
                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                )}
                {typeVisibility.careerCards !== undefined && (
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <IoDocumentTextOutline className="text-blue-500" />
                      <span className={darkMode ? 'text-white' : 'text-gray-900'}>Cards</span>
                    </div>
                    <Switch
                      checked={typeVisibility.careerCards}
                      onChange={(checked) => onTypeVisibilityChange('careerCards', checked)}
                      className={`${
                        typeVisibility.careerCards ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                      } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          typeVisibility.careerCards ? 'translate-x-5' : 'translate-x-1'
                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                )}
                {typeVisibility.reviews !== undefined && (
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <IoNewspaperOutline className="text-purple-500" />
                      <span className={darkMode ? 'text-white' : 'text-gray-900'}>Reviews</span>
                    </div>
                    <Switch
                      checked={typeVisibility.reviews}
                      onChange={(checked) => onTypeVisibilityChange('reviews', checked)}
                      className={`${
                        typeVisibility.reviews ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                      } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          typeVisibility.reviews ? 'translate-x-5' : 'translate-x-1'
                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                )}
              </div>
            )}

            <div className="max-h-60 overflow-y-auto">
              {filteredTags.length === 0 ? (
                <div className={`p-4 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {tags.length === 0 ? 'No tags available' : 'No matching tags found'}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredTags.map((tag) => (
                    <button
                      key={tag.name}
                      onClick={() => toggleTag(tag.name)}
                      className={`w-full px-3 py-2 flex items-center justify-between rounded-md ${
                        selectedTags.includes(tag.name)
                          ? darkMode
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-100 text-blue-800'
                          : darkMode
                          ? 'hover:bg-gray-700 text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      } transition-colors`}
                    >
                      <span className="flex items-center gap-2">
                        {getTagIcon(tag.types)}
                        <span>{tag.name}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TagsDropdown; 